// src/components/ParentChatLink.tsx
"use client";

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ArrowLeft } from 'lucide-react';
import FirebaseService from '@/lib/firebase';

interface ParentChatLinkProps {
  parentAssistantId?: string;
  className?: string;
}

interface ParentInfo {
  name: string;
  avatar: string;
}

export function ParentChatLink({ parentAssistantId, className = '' }: ParentChatLinkProps) {
  const [parentInfo, setParentInfo] = useState<ParentInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!parentAssistantId) {
      setLoading(false);
      return;
    }

    const fetchParentInfo = async () => {
      try {
        const parentAssistant = await FirebaseService.getAssistant(parentAssistantId);
        if (parentAssistant) {
          setParentInfo({
            name: parentAssistant.name,
            avatar: parentAssistant.avatar
          });
        }
      } catch (error) {
        console.error('Error fetching parent assistant:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchParentInfo();
  }, [parentAssistantId]);

  // Don't render if no parent or still loading
  if (!parentAssistantId || loading) {
    return null;
  }

  // Don't render if we couldn't fetch parent info
  if (!parentInfo) {
    return null;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          asChild
          className={`gap-2 ${className}`}
        >
          <Link href={`/assistant/${parentAssistantId}`}>
            <ArrowLeft className="h-4 w-4" />
            <Avatar className="h-6 w-6">
              <AvatarImage src={parentInfo.avatar} alt={parentInfo.name} />
              <AvatarFallback>{parentInfo.name[0]}</AvatarFallback>
            </Avatar>
            <span className="hidden sm:inline">Previous Chat</span>
          </Link>
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <p>Return to: {parentInfo.name}</p>
      </TooltipContent>
    </Tooltip>
  );
}
